(() => {
  if (customElements.get('sg-sticky-cart')) return;

  class SimpleGrowStickyCart extends HTMLElement {
    connectedCallback() {
      if (document.readyState === 'loading') {
        this.onReady = () => this.initialize();
        document.addEventListener('DOMContentLoaded', this.onReady, { once: true });
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      this.productInfo = this.closest('product-info');
      this.mainButton = this.productInfo?.querySelector('button[name="add"]');
      this.mainQuantity = this.productInfo?.querySelector('.quantity__input');
      this.quantity = this.querySelector('.sticky-quantity-input');
      this.addButton = this.querySelector('.sticky-add');
      this.plusButton = this.querySelector('.sticky-plus');
      this.minusButton = this.querySelector('.sticky-minus');
      if (!this.mainButton || !this.mainQuantity || !this.quantity || !this.addButton) return;

      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.sync = () => {
        for (const attribute of ['min', 'max', 'step']) {
          const value = this.mainQuantity.getAttribute(attribute);
          if (value === null) this.quantity.removeAttribute(attribute);
          else this.quantity.setAttribute(attribute, value);
        }
        this.quantity.value = this.mainQuantity.value;
        this.quantity.disabled = this.mainQuantity.disabled;
        const value = Number(this.quantity.value);
        const minimum = this.quantity.min === '' ? 1 : Number(this.quantity.min);
        const maximum = this.quantity.max === '' ? Infinity : Number(this.quantity.max);
        if (this.minusButton) this.minusButton.disabled = this.quantity.disabled || value <= minimum;
        if (this.plusButton) this.plusButton.disabled = this.quantity.disabled || value >= maximum;
        this.addButton.disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
        const label = this.mainButton.querySelector('span')?.textContent || this.mainButton.textContent;
        if (label.trim()) this.addButton.textContent = label.trim();
        this.updateVisibility();
      };
      this.updateVisibility = () => {
        this.classList.toggle('show', window.innerWidth <= 749 && this.mainButton.getBoundingClientRect().bottom < 0);
      };
      this.commitQuantity = (rawValue) => {
        const minimum = this.mainQuantity.min === '' ? 1 : Number(this.mainQuantity.min);
        const maximum = this.mainQuantity.max === '' ? Infinity : Number(this.mainQuantity.max);
        const step = Number(this.mainQuantity.step) || 1;
        let value = Number(rawValue);
        if (!Number.isFinite(value) || rawValue === '') value = minimum;
        value = minimum + Math.round((Math.max(minimum, value) - minimum) / step) * step;
        if (value > maximum) value = minimum + Math.floor((maximum - minimum) / step) * step;
        this.mainQuantity.value = Math.max(minimum, value);
        this.mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
        this.sync();
      };

      this.mainQuantity.addEventListener('input', this.sync, options);
      this.mainQuantity.addEventListener('change', this.sync, options);
      this.quantity.addEventListener('change', () => this.commitQuantity(this.quantity.value), options);
      this.plusButton?.addEventListener('click', () => {
        this.commitQuantity(Number(this.quantity.value) + (Number(this.mainQuantity.step) || 1));
      }, options);
      this.minusButton?.addEventListener('click', () => {
        this.commitQuantity(Number(this.quantity.value) - (Number(this.mainQuantity.step) || 1));
      }, options);
      this.addButton.addEventListener('click', () => {
        if (this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true') {
          this.sync();
          return;
        }
        this.commitQuantity(this.quantity.value);
        if (this.mainQuantity.checkValidity()) this.mainButton.click();
      }, options);
      window.addEventListener('scroll', this.updateVisibility, { ...options, passive: true });
      window.addEventListener('resize', this.updateVisibility, options);

      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.mainButton, { attributes: true, childList: true, characterData: true, subtree: true });
      this.observer.observe(this.mainQuantity, { attributes: true, attributeFilter: ['min', 'max', 'step', 'value', 'disabled'] });
      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.quantityUpdate, this.sync));
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) === this.productInfo.dataset.section) this.sync();
        }));
      }
      this.sync();
    }

    disconnectedCallback() {
      document.removeEventListener('DOMContentLoaded', this.onReady);
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    }
  }

  customElements.define('sg-sticky-cart', SimpleGrowStickyCart);

  // Delegation also handles FAQ sections inserted by Shopify's theme editor.
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('.fragen-zu-spider-26__question');
    const section = button?.closest('.fragen-zu-spider-26');
    const item = button?.closest('.fragen-zu-spider-26__item');
    if (!section || !item) return;
    const wasOpen = button.getAttribute('aria-expanded') === 'true';
    section.querySelectorAll('.fragen-zu-spider-26__item').forEach((otherItem) => {
      const otherButton = otherItem.querySelector('.fragen-zu-spider-26__question');
      const answer = otherItem.querySelector('.fragen-zu-spider-26__answer');
      if (!otherButton || !answer) return;
      const open = otherItem === item && !wasOpen;
      otherItem.classList.toggle('is-open', open);
      otherButton.setAttribute('aria-expanded', String(open));
      answer.hidden = !open;
    });
  });
})();
